import React from 'react';
import { AlertTriangle, Trash2, Info, CheckCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  type = 'warning', // 'warning', 'error', 'info', 'success'
  confirmText,
  cancelText,
  confirmColor = 'primary'
}) => {
  const { t } = useTranslation();
  if (!open) return null;

  const getIcon = () => {
    const iconProps = { className: 'w-12 h-12' };
    
    switch (type) {
      case 'error':
        return <Trash2 {...iconProps} className="w-12 h-12 text-red-500" />;
      case 'info':
        return <Info {...iconProps} className="w-12 h-12 text-blue-500" />;
      case 'success':
        return <CheckCircle {...iconProps} className="w-12 h-12 text-green-500" />;
      default:
        return <AlertTriangle {...iconProps} className="w-12 h-12 text-amber-500" />;
    }
  };

  const getConfirmButtonStyles = () => {
    if (confirmColor !== 'primary') {
      switch (confirmColor) {
        case 'error':
          return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
        case 'warning':
          return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500';
        case 'success':
          return 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
        default:
          return 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500';
      }
    }
    
    switch (type) {
      case 'error':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'warning':
        return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500';
      default:
        return 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Content */}
          <div className="p-6">
            {/* Icon and Title */}
            <div className="flex flex-col items-center text-center mb-4">
              <div className="mb-4">
                {getIcon()}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {title || t('common.confirmAction')}
              </h3>
            </div>
            
            {/* Message */}
            <div className="text-center mb-6">
              <p className="text-gray-600 leading-relaxed">
                {message || t('common.confirmMessage')}
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex justify-center space-x-4 rtl:space-x-reverse">
              <button
                onClick={onClose}
                className="px-6 py-2.5 min-w-[100px] border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
              >
                {cancelText || t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className={`px-6 py-2.5 min-w-[100px] rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                  getConfirmButtonStyles()
                }`}
              >
                {confirmText || t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
